type BasePageContent = {
  meta: {
    title: string;
    description: string;
    image?: string;
  };
  hero?: {
    description: string;
    backgroundImage?: string;
    heroImage?: string;
    title?: { highlight: string };
    city?: string;
  };
  eventTypes?: {
    icon: string;
    title: string;
    description?: string;
  }[];
  commonQuestions?: {
    questions: {
      question: string;
      answer: string;
    }[];
  };
  statisticsAudience?: string;
  highlightsImage?: string;
  guide?: {
    points: {
      icon: string;
      title: string;
    }[];
    guideImage: string;
    closingText: string;
  };
  eventSectionDescription?: {
    title: string;
    description: string;
  };
  featuresTarget?: string;
};

export type PageContent = BasePageContent;

export const pagesContent: Record<string, PageContent> = {
  'anime': {
    meta: {
      title: 'Host anime screenings & film events | Peatix',
      description: 'Host your first anime screenings & film events with Peatix—an all-in-one platform. No credit card required; set up in minutes, pay only when tickets sell!',
    },
    hero: {
      title: {
        highlight: 'anime screenings & film',
      },
      description: 'Japanese anime premieres, film festivals, and fan gatherings',
      backgroundImage: '/host/images/hero-bg-anime.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Anime premieres',
      },
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Film festivals',
      },
      {
        icon: '/host/images/message-heart-square.svg',
        title: 'Fan meetups',
      },
      {
        icon: '/host/images/calendar.svg',
        title: 'Movie marathons',
      },
      {
        icon: '/host/images/video-recorder.svg',
        title: 'Special screenings',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What types of events can I host?",
          answer: "Perfect for Japanese anime screenings, film festivals, fan meetups, movie marathons, and special premieres."
        }
      ]
    }
  },
  'citypop': {
    meta: {
      title: 'Host Japanese City Pop & 80s J-Pop events | Peatix',
      description: 'Bring the City Pop vibe to your community! Use our platform to organize concerts, DJ nights, and dance parties celebrating Japanese 80s music',
    },
    hero: {
      title: {
        highlight: 'Japanese City Pop & 80s J-pop',
      },
      description: 'Japanese City Pop events, City Pop parties, City Pop fan gatherings, and live City Pop performances',
      backgroundImage: '/host/images/hero-bg-citypop.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/music-note-01.svg',
        title: 'Live concerts',
        description: 'Showcase bands playing City Pop classics',
      },
      {
        icon: '/host/images/headphones-01.svg',
        title: 'DJ nights',
        description: 'Create nostalgia with vinyl sessions and dance parties',
      },
      {
        icon: '/host/images/message-heart-square.svg',
        title: 'Community gatherings',
        description: 'Bring people together to celebrate 80s Japanese music',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What is Japanese City Pop?",
          answer: "City Pop is a genre of Japanese pop music from the late 70s and 80s, blending jazz, funk, and synth-driven melodies. Its recent global resurgence has made it a favorite among music lovers and collectors worldwide."
        }
      ]
    }
  },
  'japanese-culture': {
    meta: {
      title: 'Host Japanese cultural events | Peatix',
      description: 'Easily host Japanese cultural events and grow your audience! Sell tickets, engage attendees, and build a thriving community with Peatix. No upfront costs – get started now!.',
    },
    hero: {
      title: {
        highlight: 'Japanese cultural events',
      },
      description: 'Tea ceremonies, calligraphy workshops, and traditional arts experiences',
      backgroundImage: '/host/images/hero-bg-japanese-culture.webp',
    },
    eventTypes: []
  },
  'japanese-art': {
    meta: {
      title: 'Host a Japanese art & design Event | Exhibitions & creative talks',
      description: 'Host stunning Japanese art exhibitions, design talks, and workshops. Connect with art lovers and create unique experiences with Peatix.',
    },
    hero: {
      title: {
        highlight: 'Japanese art & design',
      },
      description: 'exhibitions, creative discussions, and hands-on workshops',
      backgroundImage: '/host/images/hero-bg-art-design.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Art exhibitions',
      },
      {
        icon: '/host/images/microphone-01.svg',
        title: 'Design talks',
      },
      {
        icon: '/host/images/brush-02.svg',
        title: 'Creative workshops',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese art and design event?",
          answer: "Japanese art is a fusion of tradition and modernity, from delicate ukiyo-e prints to cutting-edge digital design. Hosting an event allows you to share this creative heritage with a passionate audience."
        }
      ]
    }
  },
  'jpop': {
    meta: {
      title: 'Host a J-Pop Event | Live Concerts, Dance Parties & Fan Meetups',
      description: 'Bring J-Pop to life with concerts, dance parties, and exclusive fan meetups. Create an unforgettable experience with Peatix.',
    },
    hero: {
      title: {
        highlight: 'J-Pop and Japanese Pop Music',
      },
      description: 'concerts, dance nights, and exclusive fan events',
      backgroundImage: '/host/images/hero-bg-jpop-06.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/music-note-01.svg',
        title: 'Live Concerts',
        description: 'J-Pop artists, idol groups, cover bands',
      },
      {
        icon: '/host/images/message-heart-square.svg',
        title: 'J-Pop Fan Meetups',
        description: 'Community gatherings, listening parties',
      },
      {
        icon: '/host/images/headphones-01.svg',
        title: 'Themed DJ Nights',
        description: 'J-Pop club events, City Pop fusion nights',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What is a J-Pop event?",
          answer: "J-Pop events showcase Japan's vibrant pop music culture through live performances, dance parties, and interactive fan experiences. They're a must for anyone who loves Japanese music!"
        }
      ]
    }
  },
  'japanese-festival': {
    meta: {
      title: 'Host a Japanese festival in NYC | Authentic culture, music & food',
      description: 'Celebrate Japan\'s rich traditions in NYC! Host a Japanese festival featuring cultural performances, street food, and live entertainment with Peatix.',
    },
    hero: {
      title: {
        highlight: 'Japanese festival in NYC',
      },
      description: 'traditional celebrations, food markets, and cultural performances',
      backgroundImage: '/host/images/hero-bg-japanese-festival.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Cultural festivals',
      },
      {
        icon: '/host/images/music-note-01.svg',
        title: 'Music performances',
      },
      {
        icon: '/host/images/heart.svg',
        title: 'Food events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What Japanese festivals happen in NYC?",
          answer: "NYC hosts a variety of Japanese festivals, from springtime Sakura Matsuri to lively summer Obon festivals featuring music, dance, and traditional street food."
        }
      ]
    }
  },
  'japanese-food': {
    meta: {
      title: 'Host unique Japanese food events with Peatix',
      description: 'Discover how to host Japanese food events that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese food',
      },
      description: 'Japanese food tastings, cooking workshops, sake pairings, and culinary experiences',
      backgroundImage: '/host/images/hero-bg-japanese-food-04.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Food tastings',
      },
      {
        icon: '/host/images/heart.svg',
        title: 'Cooking classes',
      },
      {
        icon: '/host/images/diamond-01.svg',
        title: 'Sake pairings',
      },
      {
        icon: '/host/images/globe-01.svg',
        title: 'Culinary experiences',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese food event?",
          answer: "Japanese food events attract food lovers eager to experience authentic flavors through sushi-making workshops, ramen tastings, sake pairings, and street food festivals. Hosting one allows you to engage a passionate audience while celebrating Japan's rich culinary heritage."
        }
      ]
    }
  },
  'tea-ceremony': {
    meta: {
      title: 'Host unique Japanese tea ceremony events with Peatix',
      description: 'Discover how to host Japanese tea ceremony events that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese tea ceremony',
      },
      description: 'Japanese matcha ceremony demonstrations, mindfulness experiences, and cultural workshops',
      backgroundImage: '/host/images/hero-bg-tea-ceremony.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Tea ceremonies',
      },
      {
        icon: '/host/images/globe-01.svg',
        title: 'Cultural experiences',
      },
      {
        icon: '/host/images/heart.svg',
        title: 'Mindfulness events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese tea ceremony event?",
          answer: "A Japanese tea ceremony event offers a cultural immersion into the art of mindfulness, hospitality, and tradition. Whether showcasing the delicate rituals of matcha preparation or the philosophy behind wabi-sabi, these events captivate attendees seeking an authentic experience."
        }
      ]
    }
  },
  'shodo': {
    meta: {
      title: 'Host unique Japanese calligraphy (shodo) workshops with Peatix',
      description: 'Discover how to host Japanese calligraphy (shodo) workshops that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese calligraphy (shodo)',
      },
      description: 'hands-on shodo workshops exploring the artistic expression of Japanese characters',
      backgroundImage: '/host/images/hero-bg-shodo.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/brush-01.svg',
        title: 'Calligraphy classes',
      },
      {
        icon: '/host/images/palette.svg',
        title: 'Cultural art workshops',
      },
      {
        icon: '/host/images/heart.svg',
        title: 'Shodo experiences',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese calligraphy workshop?",
          answer: "Japanese calligraphy (shodo) workshops provide a hands-on cultural experience, blending artistic expression with meditative practice. Hosting one invites participants to explore kanji brush techniques, historical significance, and the beauty of Japanese aesthetics."
        }
      ]
    }
  },
  'japanese-language': {
    meta: {
      title: 'Host unique Japanese language exchange events with Peatix',
      description: 'Discover how to host Japanese language exchange events that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese language exchange',
      },
      description: 'language meetups for practicing Japanese with native speakers and learners',
      backgroundImage: '/host/images/hero-bg-japanese-language.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/message-chat-circle.svg',
        title: 'Language meetups',
      },
      {
        icon: '/host/images/globe-01.svg',
        title: 'Cultural exchanges',
      },
      {
        icon: '/host/images/translate-01.svg',
        title: 'Japanese language learning',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese language exchange event?",
          answer: "Language exchange events create a dynamic space for learners and native speakers to practice Japanese, build fluency, and engage in cultural exchange. These events foster authentic conversations, helping attendees deepen their understanding of the language in a real-world setting."
        }
      ]
    }
  },
  'japanese-literature': {
    meta: {
      title: 'Host unique Japanese literature and book clubs with Peatix',
      description: 'Discover how to host Japanese literature and book clubs that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese literature and poetry',
      },
      description: 'book discussions featuring Japanese literature, from modern novels to classical poetry',
      backgroundImage: '/host/images/hero-bg-japanese-literature.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/book-open-02.svg',
        title: 'Book clubs',
      },
      {
        icon: '/host/images/message-heart-square.svg',
        title: 'Literary discussions',
      },
      {
        icon: '/host/images/brush-01.svg',
        title: 'Haiku poetry readings',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese literature event?",
          answer: "A Japanese literature event offers a deep dive into the works of Murakami, Kawabata, and other literary masters, sparking discussions on cultural themes, storytelling techniques, and historical narratives. These events attract book lovers looking for intellectual and cultural enrichment."
        }
      ]
    }
  },
  'manga': {
    meta: {
      title: 'Host unique manga drawing and illustration workshops with Peatix',
      description: 'Discover how to host manga drawing and illustration workshops that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Manga drawing and illustration',
      },
      description: 'manga art classes and illustration workshops',
      backgroundImage: '/host/images/hero-bg-manga.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/book-open-02.svg',
        title: 'Manga art classes',
      },
      {
        icon: '/host/images/palette.svg',
        title: 'Illustration workshops',
      },
      {
        icon: '/host/images/brush-02.svg',
        title: 'Creative drawing sessions',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a manga drawing workshop?",
          answer: "Manga drawing workshops appeal to artists and anime fans eager to learn character design, storytelling, and professional illustration techniques. Hosting one provides a hands-on opportunity to explore Japan's most influential art form while engaging creative communities."
        }
      ]
    }
  },
  'craft': {
    meta: {
      title: 'Host unique Japanese craft (kintsugi & pottery) classes with Peatix',
      description: 'Discover how to host Japanese craft (kintsugi & pottery) classes that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese craft (kintsugi & pottery)',
      },
      description: 'workshops on kintsugi (golden joinery) and traditional Japanese pottery techniques',
      backgroundImage: '/host/images/hero-bg-craft.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Craft workshops',
      },
      {
        icon: '/host/images/heart.svg',
        title: 'Pottery classes',
      },
      {
        icon: '/host/images/brush-02.svg',
        title: 'Kintsugi art sessions',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a kintsugi & pottery workshop?",
          answer: "These workshops celebrate Japanese craftsmanship, teaching pottery techniques and Kintsugi—the ancient art of repairing broken ceramics with gold. Hosting one attracts people interested in sustainability, mindful artistry, and traditional aesthetics."
        }
      ]
    }
  },
  'martialarts': {
    meta: {
      title: 'Host unique Japanese martial arts demonstrations with Peatix',
      description: 'Discover how to host Japanese martial arts demonstrations that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese martial arts demonstration',
      },
      description: 'martial arts showcases including Karate, Judo, Aikido, and Kendo',
      backgroundImage: '/host/images/hero-bg-martialarts-01.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/star-07.svg',
        title: 'Martial arts events',
      },
      {
        icon: '/host/images/shield-plus.svg',
        title: 'Self-defense workshops',
      },
      {
        icon: '/host/images/globe-01.svg',
        title: 'Cultural demonstrations',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese martial arts event?",
          answer: "Japanese martial arts events showcase disciplines like karate, judo, kendo, and aikido through live demonstrations, interactive classes, and historical discussions. Hosting one immerses participants in the philosophy, discipline, and techniques of these time-honored practices."
        }
      ]
    }
  },
  'taiko': {
    meta: {
      title: 'Host unique taiko drumming performances and workshops with Peatix',
      description: 'Discover how to host taiko drumming performances and workshops that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Taiko drumming performances and workshops',
      },
      description: 'taiko performances and hands-on drumming workshops',
      backgroundImage: '/host/images/hero-bg-taiko.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Music performances',
      },
      {
        icon: '/host/images/music-note-01.svg',
        title: 'Drumming workshops',
      },
      {
        icon: '/host/images/star-07.svg',
        title: 'Cultural events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a taiko drumming event?",
          answer: "Taiko drumming events offer a high-energy, cultural experience where attendees learn the rhythmic power of Japanese drumming. Whether through workshops or performances, these events attract music enthusiasts and cultural explorers alike."
        }
      ]
    }
  },
  'whiskey-sake': {
    meta: {
      title: 'Host unique Japanese whiskey and sake tasting events with Peatix',
      description: 'Discover how to host Japanese whiskey and sake tasting events that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese whiskey and sake tasting',
      },
      description: 'tasting sessions featuring premium Japanese whiskey and sake varieties',
      backgroundImage: '/host/images/hero-bg-whiskey-sake.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Tasting events',
      },
      {
        icon: '/host/images/star-07.svg',
        title: 'Culinary pairings',
      },
      {
        icon: '/host/images/calendar.svg',
        title: 'Whiskey and sake experiences',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese whiskey & sake tasting event?",
          answer: "These events offer a guided exploration of Japan's finest whiskey and sake, featuring expert-led tastings, flavor pairings, and insights into craftsmanship. Hosting one caters to connoisseurs and newcomers eager to experience Japan's rich drinking culture."
        }
      ]
    }
  },
  'streetwear': {
    meta: {
      title: 'Host unique Japanese streetwear and fashion pop-ups with Peatix',
      description: 'Discover how to host Japanese streetwear and fashion pop-ups that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese streetwear and fashion',
      },
      description: 'pop-up events showcasing Japanese street fashion and contemporary trends',
      backgroundImage: '/host/images/hero-bg-streetwear-01.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/calendar.svg',
        title: 'Fashion pop-ups',
      },
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Streetwear events',
      },
      {
        icon: '/host/images/stand.svg',
        title: 'Style workshops',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a Japanese streetwear event?",
          answer: "Japanese street fashion has a global influence, blending bold aesthetics, minimalism, and high-quality craftsmanship. Hosting a streetwear event connects fashion lovers with exclusive pop-ups, trend discussions, and the latest in Harajuku and Tokyo style."
        }
      ]
    }
  },
  'cosplay': {
    meta: {
      title: 'Host unique cosplay events and competitions with Peatix',
      description: 'Discover how to host cosplay events and competitions that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'cosplay competition',
      },
      description: 'cosplay meetups, contests, and photoshoots for anime and manga fans',
      backgroundImage: '/host/images/hero-bg-cosplay.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/trophy-01.svg',
        title: 'Cosplay contests',
      },
      {
        icon: '/host/images/calendar.svg',
        title: 'Anime conventions',
      },
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Pop culture events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a cosplay event?",
          answer: "Cosplay events bring together anime, manga, and gaming fans for costume showcases, competitions, and meetups. Hosting one fuels community engagement, creative expression, and the vibrant energy of otaku culture."
        }
      ]
    }
  },
  'jrock': {
    meta: {
      title: 'Host unique Japanese rock (J-Rock) and Visual kei concerts with Peatix',
      description: 'Discover how to host Japanese rock (J-Rock) and Visual kei concerts that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese rock (J-Rock) and Visual kei',
      },
      description: 'concerts and music events featuring Japanese rock and Visual kei artists',
      backgroundImage: '/host/images/hero-bg-jrock-01.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/music-note-01.svg',
        title: 'Music concerts',
      },
      {
        icon: '/host/images/message-heart-square.svg',
        title: 'J-Rock fan meetups',
      },
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Visual kei events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "Why host a J-Rock & Visual kei event?",
          answer: "J-Rock and Visual Kei events celebrate Japan's unique rock scene through concerts, performances, and fan gatherings. Whether featuring underground artists or iconic bands, these events offer an electrifying experience for music lovers."
        }
      ]
    }
  },
  'ikebana': {
    meta: {
      title: 'Host unique ikebana (Japanese flower arrangement) classes with Peatix',
      description: 'Discover how to host ikebana (Japanese flower arrangement) classes that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'ikebana (Japanese flower arrangement)',
      },
      description: 'ikebana workshops exploring the principles of Japanese flower arrangement',
      backgroundImage: '/host/images/hero-bg-ikebana-01.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/brush-02.svg',
        title: 'Art workshops',
      },
      {
        icon: '/host/images/calendar.svg',
        title: 'Flower arrangement classes',
      },
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Cultural events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What are ikebana classes?",
          answer: "Ikebana classes teach the traditional Japanese art of flower arrangement, focusing on aesthetics, balance, and cultural meaning."
        }
      ]
    }
  },
  'kawaii-culture': {
    meta: {
      title: 'Host unique kawaii culture pop-ups and events with Peatix',
      description: 'Discover how to host kawaii culture pop-ups and events that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'kawaii culture',
      },
      description: 'events showcasing kawaii fashion, art, and pop culture',
      backgroundImage: '/host/images/hero-bg-kawaii-culture.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/calendar.svg',
        title: 'Fashion pop-ups',
      },
      {
        icon: '/host/images/brush-02.svg',
        title: 'Art exhibitions',
      },
      {
        icon: '/host/images/star-07.svg',
        title: 'Kawaii lifestyle events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What are kawaii culture events?",
          answer: "These events showcase Japanese kawaii culture through fashion, art, and pop-up experiences celebrating all things cute."
        }
      ]
    }
  },
  'zen': {
    meta: {
      title: 'Japanese spirituality and Zen meditation sessions | Host unique Japanese spirituality and Zen meditation sessions with Peatix',
      description: 'Discover how to host Japanese spirituality and Zen meditation sessions that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'Japanese spirituality and Zen meditation',
      },
      description: 'mindfulness sessions exploring Japanese spirituality and Zen meditation practices',
      backgroundImage: '/host/images/hero-bg-spirituality-zen-01.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/calendar.svg',
        title: 'Meditation retreats',
      },
      {
        icon: '/host/images/stand.svg',
        title: 'Mindfulness workshops',
      },
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Spiritual events',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What are Zen meditation sessions?",
          answer: "These sessions focus on mindfulness and spiritual practices rooted in Japanese Zen traditions, promoting peace and balance."
        }
      ]
    }
  },
  'bonsai': {
    meta: {
      title: 'Host unique bonsai gardening workshops with Peatix',
      description: 'Discover how to host bonsai gardening workshops that stand out. Connect communities, share experiences, and grow your audience with Peatix. Start your event today.',
    },
    hero: {
      title: {
        highlight: 'bonsai gardening',
      },
      description: 'hands-on workshops exploring the art of Japanese bonsai gardening',
      backgroundImage: '/host/images/hero-bg-bonsai-gardening.webp',
    },
    eventTypes: [
      {
        icon: '/host/images/ticket-01.svg',
        title: 'Gardening classes',
      },
      {
        icon: '/host/images/stand.svg',
        title: 'Cultural workshops',
      },
      {
        icon: '/host/images/brush-02.svg',
        title: 'Bonsai art sessions',
      },
    ],
    commonQuestions: {
      questions: [
        {
          question: "What are bonsai gardening workshops?",
          answer: "These workshops teach the Japanese art of bonsai, focusing on tree cultivation techniques, aesthetics, and cultural significance."
        }
      ]
    }
  },
  "ca-losangeles-japanese-events": {
    meta: {
      title: "Japanese events in Los Angeles | Discover & host with Peatix",
      description: "Explore and host Japanese cultural events in Los Angeles. Connect communities with unique experiences. Start hosting today with Peatix."
    },
    hero: {
      description: "Discover Japanese festivals, cultural gatherings, and entertainment events in Los Angeles.",
      backgroundImage: "/host/images/hero-bg-ca-losangeles-japanese-events.webp",
      city: "Los Angeles"
    },
    eventTypes: [
      {
        icon: "/host/images/star-07.svg",
        title: "Cultural festivals",
      },
      {
        icon: "/host/images/message-heart-square.svg",
        title: "Community gatherings",
      },
      {
        icon: "/host/images/globe-01.svg",
        title: "Japanese events",
      }
    ],
    commonQuestions: {
      questions: [
        {
          question: "What Japanese events happen in Los Angeles?",
          answer: "Los Angeles hosts a variety of Japanese events, including cultural festivals, food tastings, and art exhibitions throughout the year."
        }
      ]
    }
  },
  "hi-honolulu-japanese-events": {
    meta: {
      title: "Japanese events in Honolulu | Discover & host with Peatix",
      description: "Explore and host Japanese cultural events in Honolulu. Connect communities with unique experiences. Start hosting today with Peatix."
    },
    hero: {
      description: "Discover Japanese festivals, cultural gatherings, and entertainment events in Honolulu.",
      backgroundImage: "/host/images/hero-bg-hi-honolulu-japanese-events.webp",
      city: "Honolulu"
    },
    eventTypes: [
      {
        icon: "/host/images/star-07.svg",
        title: "Cultural festivals",
      },
      {
        icon: "/host/images/message-heart-square.svg",
        title: "Community gatherings",
      },
      {
        icon: "/host/images/globe-01.svg",
        title: "Japanese events",
      }
    ],
    commonQuestions: {
      questions: [
        {
          question: "What Japanese events happen in Honolulu?",
          answer: "Honolulu hosts a variety of Japanese events, including cultural festivals, food tastings, and art exhibitions throughout the year."
        }
      ]
    }
  },
  "ca-sanfrancisco-japanese-events": {
    meta: {
      title: "Japanese events in San Francisco | Discover & host with Peatix",
      description: "Explore and host Japanese cultural events in San Francisco. Connect communities with unique experiences. Start hosting today with Peatix."
    },
    hero: {
      description: "Discover Japanese festivals, cultural gatherings, and entertainment events in San Francisco.",
      backgroundImage: "/host/images/hero-bg-ca-sanfrancisco-japanese-events.webp",
      city: "San Francisco"
    },
    eventTypes: [
      {
        icon: "/host/images/star-07.svg",
        title: "Cultural festivals",
      },
      {
        icon: "/host/images/message-heart-square.svg",
        title: "Community gatherings",
      },
      {
        icon: "/host/images/globe-01.svg",
        title: "Japanese events",
      }
    ],
    commonQuestions: {
      questions: [
        {
          question: "What Japanese events happen in San Francisco?",
          answer: "San Francisco hosts a variety of Japanese events, including cultural festivals, food tastings, and art exhibitions throughout the year."
        }
      ]
    }
  },
  "wa-seattle-japanese-events": {
    meta: {
      title: "Japanese events in Seattle | Discover & host with Peatix",
      description: "Explore and host Japanese cultural events in Seattle. Connect communities with unique experiences. Start hosting today with Peatix."
    },
    hero: {
      description: "Discover Japanese festivals, cultural gatherings, and entertainment events in Seattle.",
      backgroundImage: "/host/images/hero-bg-wa-seattle-japanese-events.webp",
      city: "Seattle"
    },
    eventTypes: [
      {
        icon: "/host/images/star-07.svg",
        title: "Cultural festivals",
      },
      {
        icon: "/host/images/message-heart-square.svg",
        title: "Community gatherings",
      },
      {
        icon: "/host/images/globe-01.svg",
        title: "Japanese events",
      }
    ],
    commonQuestions: {
      questions: [
        {
          question: "What Japanese events happen in Seattle?",
          answer: "Seattle hosts a variety of Japanese events, including cultural festivals, food tastings, and art exhibitions throughout the year."
        }
      ]
    }
  },
  "dc-washington-japanese-events": {
    meta: {
      title: "Japanese events in Washington | Discover & host with Peatix",
      description: "Explore and host Japanese cultural events in Washington. Connect communities with unique experiences. Start hosting today with Peatix."
    },
    hero: {
      description: "Discover Japanese festivals, cultural gatherings, and entertainment events in Washington.",
      backgroundImage: "/host/images/hero-bg-dc-washington-japanese-events.webp",
      city: "Washington"
    },
    eventTypes: [
      {
        icon: "/host/images/star-07.svg",
        title: "Cultural festivals",
      },
      {
        icon: "/host/images/message-heart-square.svg",
        title: "Community gatherings",
      },
      {
        icon: "/host/images/globe-01.svg",
        title: "Japanese events",
      }
    ],
    commonQuestions: {
      questions: [
        {
          question: "What Japanese events happen in Washington?",
          answer: "Washington hosts a variety of Japanese events, including cultural festivals, food tastings, and art exhibitions throughout the year."
        }
      ]
    }
  },
  "japanese-organizers-us": {
    meta: {
      title: "アメリカで広げる日本文化｜イベント開催・コミュニティ運営ならPeatix",
      description: "Peatixはアメリカで日本文化を発信したい人をサポート。イベント作成、参加者管理、コミュニティ機能まで、すべて無料で使えます。"
    },

  }
}; 